import type { VoidFunctionComponent } from 'react';

import { EMAIL_RESEND_COOLDOWN_SECONDS } from '../../../../shared/MfaManager/consts';
import { useResendEmailLoginCode } from '../../../hooks/api/auth/mfa/useResendEmailLoginCode';
import { useResendCooldown } from '../../../hooks/useResendCooldown';
import Button from '../../generic/Button/Button';
import { BUTTON_TYPE } from '../../generic/Button/button-constants';
import VerificationCodeInput from '../../generic/Forms/VerificationCodeInput';
import { useAuthContext } from '../context/AuthContext';

/**
 * Email MFA verification form.
 * Displays code input and resend button.
 */
const EmailMfaVerification: VoidFunctionComponent = () => {
  const { mfaCode, submitButton, statusMethods, status } = useAuthContext();
  // Started on mount: this form only renders once the backend has issued the challenge and mailed a code, so
  // the server's resend window is already running.
  const resendCooldown = useResendCooldown(EMAIL_RESEND_COOLDOWN_SECONDS, { startOnMount: true });
  const resendMutation = useResendEmailLoginCode();

  const handleResend = async () => {
    if (resendCooldown.isCoolingDown || resendMutation.isPending) {
      return;
    }

    statusMethods.onPending();
    try {
      await resendMutation.mutateAsync();
      statusMethods.onSuccess();
      resendCooldown.start();
    } catch (error) {
      statusMethods.onError(error);
    }
  };

  return (
    <>
      <p className="text-center margin-bottom-md">
        Enter the 6-digit verification code sent to your email.
      </p>
      <VerificationCodeInput
        value={mfaCode.value}
        onChange={mfaCode.onChange}
        onComplete={submitButton.onSubmit}
        disabled={status.isLoading}
      />
      <div className="text-center margin-top-md">
        <Button
          type={BUTTON_TYPE.ANCHOR}
          onClick={handleResend}
          isDisabled={resendCooldown.isCoolingDown || resendMutation.isPending}
        >
          {resendCooldown.isCoolingDown
            ? `Resend code (${resendCooldown.secondsRemaining}s)`
            : 'Resend code'}
        </Button>
      </div>
    </>
  );
};

export default EmailMfaVerification;
